import { CompoundEntityRef } from '@backstage/catalog-model';
import { InsightFacts } from '@backstage-community/plugin-tech-insights-common';
import { TechInsightsApi } from '@backstage-community/plugin-tech-insights-react';
import { buildMockInsightFacts } from './mockScorecardFacts';

const SCORECARD_FACT_IDS = [
  'team-lint-rollup',
  'catalog-hygiene',
  'team-profile-completeness',
];

/**
 * Decorates a real `TechInsightsApi` so the three `plugins/scorecard` facts
 * are served from `buildMockInsightFacts` instead of the backend — gated by
 * `scorecard.mockFacts` in app-config (see apis.ts) so this never runs
 * outside local dev. Every other fact id and every other method delegates to
 * the wrapped client unchanged, so non-scorecard Tech Insights usage (e.g.
 * the portfolio-linter check card on Component pages) is unaffected.
 */
export class MockScorecardTechInsightsClient implements TechInsightsApi {
  constructor(private readonly delegate: TechInsightsApi) {}

  getCheckResultRenderers: TechInsightsApi['getCheckResultRenderers'] = types =>
    this.delegate.getCheckResultRenderers(types);

  isCheckResultFailed: TechInsightsApi['isCheckResultFailed'] = check =>
    this.delegate.isCheckResultFailed(check);

  getAllChecks(): ReturnType<TechInsightsApi['getAllChecks']> {
    return this.delegate.getAllChecks();
  }

  runChecks(
    entityParams: CompoundEntityRef,
    checks?: string[],
  ): ReturnType<TechInsightsApi['runChecks']> {
    return this.delegate.runChecks(entityParams, checks);
  }

  runBulkChecks(
    ...args: Parameters<TechInsightsApi['runBulkChecks']>
  ): ReturnType<TechInsightsApi['runBulkChecks']> {
    return this.delegate.runBulkChecks(...args);
  }

  getFactSchemas(): ReturnType<TechInsightsApi['getFactSchemas']> {
    return this.delegate.getFactSchemas();
  }

  getLinksForEntity: TechInsightsApi['getLinksForEntity'] = (...args) =>
    this.delegate.getLinksForEntity(...args);

  async getFacts(
    entity: CompoundEntityRef,
    facts: string[],
  ): Promise<InsightFacts> {
    const mockableFactIds = facts.filter(id =>
      SCORECARD_FACT_IDS.includes(id),
    );
    const passthroughFactIds = facts.filter(
      id => !SCORECARD_FACT_IDS.includes(id),
    );

    const [mocked, real] = await Promise.all([
      mockableFactIds.length
        ? Promise.resolve(
            buildMockInsightFacts(mockableFactIds, entity.name),
          )
        : Promise.resolve({} as InsightFacts),
      passthroughFactIds.length
        ? this.delegate.getFacts(entity, passthroughFactIds)
        : Promise.resolve({} as InsightFacts),
    ]);

    return { ...real, ...mocked };
  }
}
